/**
 * Activity Log Service
 * Handles spreadsheet audit trail operations
 */

import { supabase } from './supabase';
import { rowUpdateStamp } from './spreadsheet-row-updates';
import type { Json } from './database.types';
import {
  SECRET_MASK,
  maskAuditValues,
  stripMaskedFields,
} from './auditMasking';

export type AuditActionType =
  | 'create'
  | 'update'
  | 'insert'
  | 'delete'
  | 'restore_row'
  | 'restore_cell'
  | 'clear_cell';

export interface AuditLogEntry {
  id: string;
  spreadsheet_id: string;
  user_id: string;
  action_type: AuditActionType;
  row_id: string | null;
  column_name: string | null;
  old_value: any;
  new_value: any;
  metadata: {
    legal_business_name?: string;
    account_order?: number | string;
    account_status?: number | string;
    user_name?: string;
    user_role?: string;
  };
  created_at: string;
  undone_at: string | null;
  undone_by: string | null;
}

export interface AuditLogEntryWithProfile extends AuditLogEntry {
  profiles: {
    name: string;
    email: string;
  };
  undone_by_profile?: {
    name: string;
    email: string;
  };
}

/**
 * Fetch activity log for a spreadsheet
 * @param spreadsheetId - The spreadsheet ID
 * @param limit - Maximum number of entries to fetch (default 50)
 * @param offset - Offset for pagination (default 0)
 */
export async function getSpreadsheetActivity(
  spreadsheetId: string,
  limit: number = 50,
  offset: number = 0
): Promise<AuditLogEntryWithProfile[]> {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const { data, error } = await supabase
    .from('spreadsheet_audit_log')
    .select(
      `
      *,
      profiles:user_id (name, email),
      undone_by_profile:undone_by (name, email)
    `
    )
    .eq('spreadsheet_id', spreadsheetId)
    .gte('created_at', thirtyDaysAgo.toISOString())
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) {
    throw new Error(`Failed to fetch activity log: ${error.message}`);
  }

  return (data as any[]).map(entry => ({
    ...entry,
    profiles: Array.isArray(entry.profiles)
      ? entry.profiles[0]
      : entry.profiles,
    undone_by_profile: Array.isArray(entry.undone_by_profile)
      ? entry.undone_by_profile[0]
      : entry.undone_by_profile,
  }));
}

/**
 * Log an audit entry
 * @param entry - The audit log entry data
 */
export async function logAuditEntry(
  entry: Omit<AuditLogEntry, 'id' | 'created_at' | 'undone_at' | 'undone_by'>
): Promise<string | null> {
  // Every role can read the audit log: never store secret values in it
  const masked = maskAuditValues(
    entry.column_name,
    entry.old_value,
    entry.new_value
  );
  const { data, error } = await supabase
    .from('spreadsheet_audit_log')
    .insert({
      spreadsheet_id: entry.spreadsheet_id,
      user_id: entry.user_id,
      action_type: entry.action_type,
      row_id: entry.row_id,
      column_name: entry.column_name,
      old_value: masked.old_value as Json,
      new_value: masked.new_value as Json,
      metadata: entry.metadata,
    })
    .select('id')
    .single();

  if (error) {
    console.error('Failed to log audit entry:', error);
    // Don't throw - audit logging should not break user operations
    return null;
  }

  return data?.id || null;
}

/**
 * Undo an audit log action
 * @param activityId - The audit log entry ID to undo
 * @param userId - The user performing the undo
 */
export async function undoActivity(
  activityId: string,
  userId: string
): Promise<AuditLogEntry> {
  // First, fetch the audit entry to verify permissions
  const { data: entry, error: fetchError } = await supabase
    .from('spreadsheet_audit_log')
    .select('*')
    .eq('id', activityId)
    .maybeSingle();

  if (fetchError) {
    throw new Error(`Failed to fetch activity: ${fetchError.message}`);
  }

  if (!entry) {
    throw new Error('Activity not found');
  }

  if (entry.undone_at) {
    throw new Error('This activity has already been undone');
  }

  // Check if action is older than 30 days
  const actionDate = new Date(entry.created_at);
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  if (actionDate < thirtyDaysAgo) {
    throw new Error('Cannot undo actions older than 30 days');
  }

  // Mark as undone
  const { error: updateError } = await supabase
    .from('spreadsheet_audit_log')
    .update({
      undone_at: new Date().toISOString(),
      undone_by: userId,
    })
    .eq('id', activityId);

  if (updateError) {
    throw new Error(`Failed to undo activity: ${updateError.message}`);
  }

  // Fetch the updated record
  const { data: updated, error: fetchUpdatedError } = await supabase
    .from('spreadsheet_audit_log')
    .select('*')
    .eq('id', activityId)
    .maybeSingle();

  if (fetchUpdatedError) {
    throw new Error(
      `Failed to fetch updated activity: ${fetchUpdatedError.message}`
    );
  }

  if (!updated) {
    throw new Error('Failed to fetch updated activity record');
  }

  return updated as AuditLogEntry;
}

/**
 * Restore a previous value (undo implementation)
 * This function handles the actual data restoration
 * @param entry - The audit log entry to restore
 */
export async function restoreFromActivity(entry: AuditLogEntry): Promise<void> {
  if (!entry.row_id) {
    throw new Error('Cannot restore: no row ID specified');
  }

  switch (entry.action_type) {
    case 'update':
    case 'clear_cell':
      // Restore old cell value
      if (!entry.column_name) {
        throw new Error('Cannot restore: no column specified');
      }
      if (entry.old_value === SECRET_MASK) {
        throw new Error(
          'Cannot restore: secret fields are not stored in the activity log'
        );
      }

      // First, get the current row data
      const { data: currentRow, error: fetchError } = await supabase
        .from('spreadsheet_rows')
        .select('row_data')
        .eq('id', entry.row_id)
        .maybeSingle();

      if (fetchError) {
        throw new Error(`Cannot restore: ${fetchError.message}`);
      }

      if (!currentRow) {
        throw new Error('Cannot restore: row not found');
      }

      // Update the specific column in the row_data JSONB
      const updatedRowData = {
        ...(currentRow.row_data as { [key: string]: Json | undefined }),
        [entry.column_name]: entry.old_value,
      };

      const { error: updateError } = await supabase
        .from('spreadsheet_rows')
        .update({
          row_data: updatedRowData,
          ...(await rowUpdateStamp()),
        })
        .eq('id', entry.row_id);

      if (updateError) {
        throw new Error(`Failed to restore cell: ${updateError.message}`);
      }
      break;

    case 'delete':
      // Restore deleted row
      // Note: This requires the old_value to contain the full row data
      if (!entry.old_value) {
        throw new Error('Cannot restore: no row data available');
      }

      const { error: insertError } = await supabase
        .from('spreadsheet_rows')
        .insert({
          id: entry.row_id,
          spreadsheet_id: entry.spreadsheet_id,
          // Masked secrets were never logged; restore the row without them
          row_data: stripMaskedFields(entry.old_value) as Json,
          row_order:
            entry.metadata.account_order ||
            (entry.old_value as any)['Account Order'] ||
            0,
        });

      if (insertError) {
        throw new Error(`Failed to restore row: ${insertError.message}`);
      }
      break;

    case 'insert':
      // Delete the inserted row
      const { error: deleteError } = await supabase
        .from('spreadsheet_rows')
        .delete()
        .eq('id', entry.row_id);

      if (deleteError) {
        throw new Error(`Failed to undo insert: ${deleteError.message}`);
      }
      break;

    default:
      throw new Error(`Cannot undo action type: ${entry.action_type}`);
  }
}

/**
 * Check if a row still exists
 * @param rowId - The row ID to check
 */
export async function checkRowExists(rowId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('spreadsheet_rows')
    .select('id')
    .eq('id', rowId)
    .maybeSingle();

  return !error && data !== null;
}
